import type { CustomViewTarget } from './customViewConfig'
import type { FilterGroup, ViewMode } from './ProductsContext'
import type { FacetFilters } from '@/types/catalog'
import type { CountryItem } from '@/types/countries'

export const facetParameters: (keyof FacetFilters)[] = [
  'product_natures',
  'brands',
  'sports',
  'sports_groups',
  'dmi_segmentations',
  'genders',
  'product_statuses',
  'ecodesign_statuses',
  'design_centers',
  'production_countries',
  'selected_customs_zones',
  'homologation_statuses',
  'validation_statuses',
  'homologation_countries',
]

export function normalizeFacetFilters(filters: FacetFilters): FacetFilters {
  return Object.fromEntries(
    Object.entries(filters)
      .map(([name, values]) => [name, [...new Set(values)].sort((left, right) => String(left).localeCompare(String(right)))])
      .filter(([, values]) => values.length > 0),
  ) as FacetFilters
}

function getHomologationCountriesFromSearchParams(countries: CountryItem[], values: string[]): string[] {
  const countryCodes = new Map(countries.map(country => [country.code.toUpperCase(), country.code.toUpperCase()]))

  return values
    .map(value => countryCodes.get(value.trim().toUpperCase()))
    .filter((value): value is string => value !== undefined)
}

export function getFacetFiltersFromSearchParams(searchParams: URLSearchParams, countries: CountryItem[]): FacetFilters {
  const homologationCountries = getHomologationCountriesFromSearchParams(countries, searchParams.getAll('homologation_countries'))

  const filters = facetParameters.reduce<FacetFilters>((result, parameterName) => {
    const values = searchParams.getAll(parameterName)

    if (values.length > 0) {
      if (parameterName === 'homologation_countries') {
        result[parameterName] = homologationCountries
      }
      else {
        result[parameterName] = values
      }
    }

    return result
  }, {})

  return normalizeFacetFilters(filters)
}

export function getInvalidFacetSelections(searchParams: URLSearchParams, filterGroups: FilterGroup[]) {
  return filterGroups.flatMap((group) => {
    const { id } = group
    const selectedValues = searchParams.getAll(id)
    const availableValues = new Set(group.filters.map(filter => filter.id))

    return selectedValues.some(value => !availableValues.has(value))
      ? [{ id, availableValues }]
      : []
  })
}

export function getEffectiveViewMode(
  customViewTarget: CustomViewTarget,
  requestedViewMode: string | null,
  currentViewMode: ViewMode,
): ViewMode {
  if (customViewTarget !== 'gallery')
    return 'sm'

  if (requestedViewMode === 'model')
    return 'model'

  if (requestedViewMode === 'sm')
    return 'sm'

  return currentViewMode
}

export function getFacetFiltersForView(
  searchParams: URLSearchParams,
  countries: CountryItem[],
  isHomologationCountryFilterAvailable: boolean,
) {
  const facetSearchParams = new URLSearchParams(searchParams)
  if (!isHomologationCountryFilterAvailable)
    facetSearchParams.delete('homologation_countries')

  return getFacetFiltersFromSearchParams(facetSearchParams, countries)
}

export function getVisibleFilterGroups(
  filterGroups: FilterGroup[],
  isHomologationCountryFilterAvailable: boolean,
): FilterGroup[] {
  if (isHomologationCountryFilterAvailable)
    return filterGroups

  return filterGroups.filter(group => group.id !== 'homologation_countries')
}
